import React, { useState } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Rnd } from 'react-rnd';

import EditActivityModal from './EditActivityModal.jsx'; // Seu modal de edição

// Cada coluna tem um 'x' inicial (distância da esquerda) e 'width'
const columns = [
  { id: 'todo', title: 'A Fazer', x: 0, width: 300 },
  { id: 'inprogress', title: 'Em Progresso', x: 320, width: 300 },
  { id: 'done', title: 'Concluído', x: 640, width: 300 }
];

// Mapeamento de prioridades para cores
const priorityColors = {
  Baixa: '#a5d6a7',   // verde claro
  Média: '#ffe082',  // amarelo claro
  Alta: '#ef9a9a'    // vermelho claro
};

// Espaçamento vertical entre cards + margem inicial
const CARD_VERTICAL_SPACING = 100;
const COLUMN_TOP_OFFSET = 50;

// -- Reordena um conjunto de cards de UMA coluna, na vertical
function reorderColumn(cardsOfColumn, columnId) {
  // Ordena em ordem crescente de Y
  const sorted = [...cardsOfColumn].sort((a, b) => a.y - b.y);
  // Encontra info da coluna (x base)
  const colInfo = columns.find((col) => col.id === columnId);

  // Empilha verticalmente
  return sorted.map((card, index) => ({
    ...card,
    x: colInfo.x + 20,
    y: COLUMN_TOP_OFFSET + index * CARD_VERTICAL_SPACING
  }));
}

// -- Reordena TODO o board (todas as colunas e todos os cards)
function reorderWholeBoard(allCards) {
  let newCards = [];
  columns.forEach((col) => {
    const colCards = allCards.filter((c) => c.column === col.id);
    const reordered = reorderColumn(colCards, col.id);
    newCards = [...newCards, ...reordered];
  });
  return newCards;
}

// -- Reordena APENAS os cards de um desenvolvedor específico
//    (mantém os outros cards como estão)
function reorderOnlyDev(allCards, devName) {
  // Se devName é "Todos", equivale a reordenar tudo
  if (devName === 'Todos') {
    return reorderWholeBoard(allCards);
  }

  // Se for um dev específico, separam os cards do dev e dos outros
  const devCards = allCards.filter((c) => c.assignedDev === devName);
  const otherCards = allCards.filter((c) => c.assignedDev !== devName);

  let rearrangedDev = [];
  columns.forEach((col) => {
    const colDevCards = devCards.filter((c) => c.column === col.id);
    const reordered = reorderColumn(colDevCards, col.id);
    rearrangedDev = [...rearrangedDev, ...reordered];
  });

  // Junta novamente: cards do dev re-empilhados + cards que não são do dev
  return [...otherCards, ...rearrangedDev];
}

function TrelloLikeWithReactRnd() {
  // Lista inicial de cards
  const [cards, setCards] = useState([
    {
      id: 'task1',
      name: 'Atividade 1',
      description: 'Descrição da atividade 1',
      priority: 'Média',
      assignedDev: 'Alice',
      x: 20,
      y: 50,
      column: 'todo'
    },
    {
      id: 'task2',
      name: 'Atividade 2',
      description: 'Descrição da atividade 2',
      priority: 'Alta',
      assignedDev: 'Bob',
      x: 20,
      y: 150,
      column: 'todo'
    },
    {
      id: 'task3',
      name: 'Atividade 3',
      description: 'Descrição da atividade 3',
      priority: 'Baixa',
      assignedDev: 'Carlos',
      x: 340,
      y: 50,
      column: 'inprogress'
    }
  ]);

  // Modal de edição
  const [selectedCard, setSelectedCard] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Filtro de desenvolvedor
  const [selectedDeveloper, setSelectedDeveloper] = useState('Todos');
  const developers = ['Todos', 'Alice', 'Bob', 'Carlos'];

  // Abre modal ao clicar no card
  const handleCardClick = (card) => {
    setSelectedCard(card);
    setModalOpen(true);
  };

  // Fecha modal (cancelar)
  const handleCloseModal = () => {
    setSelectedCard(null);
    setModalOpen(false);
  };

  // Salva alterações do card (modal)
  const handleSaveCard = (updatedCard) => {
    setCards((prevCards) =>
      prevCards.map((c) => (c.id === updatedCard.id ? updatedCard : c))
    );
    setSelectedCard(null);
    setModalOpen(false);
  };

  // Ao trocar de desenvolvedor no filtro,
  // re-empilhamos os cards do dev escolhido (ou todos)
  const handleChangeDeveloper = (e) => {
    const newDev = e.target.value;
    setSelectedDeveloper(newDev);

    // Re-empilha de acordo com esse dev
    setCards((prev) => reorderOnlyDev(prev, newDev));
  };

  // Lida com o fim do drag-and-drop
  const handleDragStop = (e, d, cardId) => {
    setCards((prevCards) => {
      const card = prevCards.find((c) => c.id === cardId);
      if (!card) return prevCards;

      const oldColumn = card.column;
      // Descobre coluna final
      let newColumnId = oldColumn;
      for (let col of columns) {
        if (d.x >= col.x && d.x < col.x + col.width) {
          newColumnId = col.id;
          break;
        }
      }

      if (oldColumn === newColumnId) {
        // Mesma coluna: re-empilha dentro dela
        const cardsSameColumn = prevCards.filter(
          (c) => c.column === oldColumn && c.id !== cardId
        );
        // Acha índice de inserção (ordem vertical)
        const sorted = [...cardsSameColumn].sort((a, b) => a.y - b.y);
        let insertionIndex = 0;
        for (let i = 0; i < sorted.length; i++) {
          if (d.y < sorted[i].y) {
            insertionIndex = i;
            break;
          }
          insertionIndex = i + 1;
        }

        // Remove do array
        let newArr = prevCards.filter((c) => c.id !== cardId);
        // Cria nova cópia do card com a posição bruta
        const updatedCard = { ...card, x: d.x, y: d.y };

        // Insere no array local
        sorted.splice(insertionIndex, 0, updatedCard);

        // Reordena a coluna
        const reorderedCol = reorderColumn(sorted, oldColumn);
        // Cards das outras colunas
        const otherCards = newArr.filter((c) => c.column !== oldColumn);

        newArr = [...otherCards, ...reorderedCol];
        return newArr;
      } else {
        // Trocou de coluna
        const oldColumnCards = prevCards.filter(
          (c) => c.column === oldColumn && c.id !== cardId
        );
        const reorderedOld = reorderColumn(oldColumnCards, oldColumn);

        const newColumnCards = prevCards.filter(
          (c) => c.column === newColumnId
        );
        // Ordena a nova coluna
        const sorted = [...newColumnCards].sort((a, b) => a.y - b.y);

        let insertionIndex = 0;
        for (let i = 0; i < sorted.length; i++) {
          if (d.y < sorted[i].y) {
            insertionIndex = i;
            break;
          }
          insertionIndex = i + 1;
        }

        const updatedCard = { ...card, x: d.x, y: d.y, column: newColumnId };
        sorted.splice(insertionIndex, 0, updatedCard);

        const reorderedNew = reorderColumn(sorted, newColumnId);

        const otherCards = prevCards.filter(
          (c) => c.column !== oldColumn && c.column !== newColumnId
        );
        return [...otherCards, ...reorderedOld, ...reorderedNew];
      }
    });
  };

  // Filtra cards do dev escolhido para renderizar
  const filteredCards = cards.filter((card) => {
    if (selectedDeveloper === 'Todos') return true;
    return card.assignedDev === selectedDeveloper;
  });

  return (
    /* Tela cheia */
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Barra superior */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 60,
          borderBottom: '1px solid #ccc',
          display: 'flex',
          alignItems: 'center',
          px: 2,
          backgroundColor: '#f9f9f9'
        }}
      >
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Board Trello-Like
        </Typography>
        {/* Filtro de desenvolvedor */}
        <FormControl size="small" sx={{ width: 200 }}>
          <InputLabel id="dev-filter-label">Desenvolvedor</InputLabel>
          <Select
            labelId="dev-filter-label"
            label="Desenvolvedor"
            value={selectedDeveloper}
            onChange={handleChangeDeveloper}
          >
            {developers.map((dev) => (
              <MenuItem key={dev} value={dev}>
                {dev}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Área do board */}
      <Box
        sx={{
          position: 'absolute',
          top: 60, // altura da barra
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#fff'
        }}
      >
        {/* Colunas */}
        {columns.map((col) => (
          <Box
            key={col.id}
            sx={{
              position: 'absolute',
              left: col.x,
              top: 0,
              width: col.width,
              height: '100%',
              borderRight: '1px solid #ddd',
              p: 1
            }}
          >
            <Typography variant="h6" align="center">
              {col.title}
            </Typography>
          </Box>
        ))}

        {/* Cards filtrados */}
        {filteredCards.map((card) => (
          <Rnd
            key={card.id}
            size={{ width: 260, height: 100 }}
            position={{ x: card.x, y: card.y }}
            onDragStop={(e, d) => handleDragStop(e, d, card.id)}
            bounds="parent"
            style={{
              backgroundColor: priorityColors[card.priority] || '#fff',
              padding: 8,
              borderRadius: 4,
              boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
              cursor: 'move'
            }}
            onClick={() => handleCardClick(card)}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {card.name}
            </Typography>
            <Typography variant="body2">{card.description}</Typography>
            <Typography variant="body2">Dev: {card.assignedDev}</Typography>
            <Typography variant="body2">
              Prioridade: {card.priority}
            </Typography>
          </Rnd>
        ))}

        {/* Legenda de Prioridade */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            left: 10,
            display: 'flex',
            gap: 2
          }}
        >
          {Object.entries(priorityColors).map(([priority, color]) => (
            <Box key={priority} sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: color,
                  borderRadius: '50%',
                  mr: 1
                }}
              />
              <Typography variant="body2">{priority}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Modal de edição */}
      <EditActivityModal
        open={modalOpen}
        onClose={handleCloseModal}
        card={selectedCard}
        onSave={handleSaveCard}
      />
    </Box>
  );
}

export default TrelloLikeWithReactRnd;
